/* globals Dashboard, Knitiv */

import APIV5 from '~/models/api';
import { BaseController } from '~/utils/base-controller';
import Utils from '~/scripts/services/utils';
import { createWidget } from './models/widget';

export default class DashboardController extends BaseController {
	/** @type {?import("gridstack").GridStack} */
	gridstack;

	/** @type {import('~/components/gridstack/gridstack.controller').default['options']} */
	gridOptions = {
		staticGrid: true,
	};

	/** @type {?import("gridstack").GridStackWidget[]} */
	layoutSave = null;

	/** @type {Knitiv.Scope} */
	$scope;

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {Knitiv.Scope} $scope
	 */
	constructor(
		$injector,
		$scope,
	) {
		super($injector);

		this.$scope = $scope;

		this.gridstack = null;

		/** @type {Record<number, Dashboard.BaseWidget>} */
		this.widgets = {};

		this.isEditMode = false;
		this.isReady = false;

		this.name = '';

		/** @type {string | undefined} */
		this.target = undefined;

		/** @type {import('@knitiv/api-client-javascript').KNode | undefined} */
		this.kid = undefined;

		this.$onInit();
	}

	toggleEditMode() {
		this.isEditMode = !this.isEditMode;

		// @ts-ignore
		this.layoutSave = this.gridstack.save(false, false);

		this.gridstack?.setStatic(!this.isEditMode);
	}

	cancelSave() {
		if (this.layoutSave) {
			this.gridstack?.load(this.layoutSave);
			this.toggleEditMode();
		}
	}

	async saveLayout() {
		this.toggleEditMode();

		/** @type {import("gridstack").GridStackOptions} */
		// @ts-ignore
		const layout = this.gridstack.save(false, true);
		// eslint-disable-next-line no-console
		console.log('layout', layout);

		if (this.kid) {
			await this.api.settings.set({
				id:       'layout',
				node_kid: this.kid,
				kjson:    JSON.stringify(layout),
			});

			this.Notification.success({
				title:   'Sauvegarde effectuée',
				message: 'La mise en forme du tableau de bord à été enregistrée avec succes!',
			});
		}
	}

	async $onInit() {
		this.kid = this.$routeParams.kid;
		this.target = this.$routeParams.widget;

		/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'widget' | 'type' | 'order' | 'dashboardName'>} */
		const queryBuilder = APIV5.instance.createQueryBuilder();

		queryBuilder
			.select(selectBuilder => selectBuilder
				.addColumn(columnBuilder => columnBuilder
					.as('dashboardName')
					.withTarget('K_NODE;DASHBOARD')
					.withPath(pathBuilder => pathBuilder
						.addFlow('up', 'K_NODE;DASHBOARD', undefined, 'END', 'K_NODE;COMPOSED_OF', 'K_NODE;WIDGET'),
					),
				)
				.addColumn(columnBuilder => columnBuilder
					.as('widget')
					.withTarget('K_NODE;WIDGET'),
				)
				.addColumn(columnBuilder => columnBuilder
					.as('type')
					.withTarget('K_NODE;WIDGETTYPE')
					.withPath(pathBuilder => pathBuilder
						.flowDown('K_NODE;WIDGETTYPE'),
					),
				)
				.addColumn(column => column
					.as('order')
					.withTarget('K_NODE;INTEGER')
					.withType('K_NODE;INTEGER')
					.withPath(path => path
						.flowDown('K_NODE;INTEGER', 'K_NODE;RANK'),
					),
				),
			)
			.from(fromBuilder => fromBuilder
				.addElement('K_NODE;WIDGET'),
			)
			.where(whereBuilder => whereBuilder
				.addRule(ruleBuilder => ruleBuilder
					.equalKid('K_NODE;DASHBOARD', this.$routeParams.kid)
					.withPath(pathBuilder => pathBuilder
						.flowUp('K_NODE;DASHBOARD'),
					),
				),
			);

		const widgetResponse = await queryBuilder.fetchAll(
			undefined,
			undefined,
			{ id: 'dashboard.widget-response' },
		);

		const queryWidgets = widgetResponse.resolved().rows.map(row => ({
			widget: row.widget[0],
			type: row.type[0],
			order: Number.parseInt(row.order[0].name, 10),
			dashboardName: `${row.dashboardName[0].name}`,
		}));

		this.name = queryWidgets[0].dashboardName || '';

		this.$rootScope.title = `${this.name} - ${this.$rootScope.title}`;

		const settings = await this.api.settings.get({
			id: 'layout',
			node_kid: this.$routeParams.kid,
		}, { id: 'dashboard.init' });

		/** @type {import("gridstack").GridStackOptions} */
		// eslint-disable-next-line prefer-destructuring
		const dashboard = Utils.JSONParseSafe(settings?.kjson);
		const values = [...queryWidgets.values()]
			.sort((a, b) => a.order - b.order);

		let index = 0;
		for (const result of values) {
			const type = result.type.kid;
			const widgetKID = result.widget.kid;

			if (this.target !== undefined && widgetKID !== this.target) {
				// next one
				// eslint-disable-next-line no-continue
				continue;
			}

			if (type) {
				const widget = await createWidget(type, widgetKID, {
					$timeout:      this.$timeout,
					$location:     this.$location,
					$injector:     this.$injector,
					$rootScope:    this.$rootScope,
					$compile:      this.$compile,
					fromDashboard: true,
					dashboardKid:  this.kid,
				});

				this.widgets[index] = widget;
				index += 1;
			}
		}

		for (const widget of Object.values(this.widgets)) {
			if (!widget) {
				console.error('No widget !');
				return;
			}

			this.gridstack?.setAnimation(true);

			const widgets = {
				ktable: 'ktable-widget',
				'pie-chart': 'pie-chart-widget',
				'gauge-chart': 'gauge-chart-widget',
				progress: 'progress-widget', //
			};

			if (!(widget.type in widgets)) {
				console.error(`Unknown widget type: ${widget.type}`);
				return;
			}

			const htmlTag = widgets[widget.type];

			const scope = this.$rootScope.$new(true);

			// @ts-ignore
			scope.$ctrl = {
				parent: this,
				widget,
			};

			const compiled = this.$compile(
				`<${htmlTag}
				widget-id="${widget.kid}"
				class="grid-stack-item"
				widget="$ctrl.widget"
				parent="$ctrl.parent"
			></${htmlTag}>`,
			)(scope);
			const element = compiled.get(0);

			/** @type {any} */
			let entry = {};

			if (dashboard && dashboard.children) {
				entry = dashboard.children.find(child => child.id === widget.kid);
			}

			/** @type {import("gridstack").GridStackWidget} */
			let widgetOptions = {
				w: 12,
				h: 20,
				x: undefined,
				y: undefined,
				id: widget.kid,
			};

			if (entry) {
				widgetOptions = {
					...widgetOptions,
					...entry,
				};
			}

			this.gridstack?.batchUpdate();

			if (element) {
				if (this.target) {
					this.$timeout(() => {
						const $element = document.querySelector('.view-list-dashboard .single-element');

						if ($element) {
							$element.append(element);
						}
					});
				}
				else {
					const item = this.gridstack?.addWidget(element, widgetOptions);
					widget.gridstackItem = item;
				}
			}
			else {
				throw new Error('Template not compiled');
			}

			this.gridstack?.commit();
		}

		this.$timeout(() => {
			this.isReady = true;

			// reference
			const scroll = document.documentElement.scrollTop;

			// console.log('scroll', scroll);

			// eslint-disable-next-line angular/interval-service
			const interval = setInterval(() => {
				const selectedId = this.$routeParams.widget;
				const newScroll = document.documentElement.scrollTop;

				// console.log('newScroll', newScroll);

				if (newScroll !== scroll) {
					clearInterval(interval);
					return;
				}

				if (selectedId) {
					const found = document.querySelectorAll(`.grid-stack-item[widget-id="${selectedId}"]`);
					if (found.length > 0) {
						const targetNode = found[0];

						// console.log('Scrolling !', targetNode);
						// targetNode.scrollIntoView({ behavior: 'smooth' });
						const y = targetNode.getBoundingClientRect().top + window.pageYOffset - 50;

						window.scrollTo({ top: y, behavior: 'smooth' });
					}
				}
			}, 50);

			// eslint-disable-next-line angular/timeout-service
			const giveUp = setTimeout(() => {
				clearInterval(interval);
			}, 15_000);

			// Quitter la page avant la fin du scroll laissait tourner la boucle, qui
			// interrogeait le DOM toutes les 50 ms en retenant ce contrôleur et ses
			// widgets jusqu'à l'échéance des 15 s.
			this.$scope.$on('$destroy', () => {
				clearInterval(interval);
				clearTimeout(giveUp);
			});
		});
	}
}
